import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { filterLocationsAtom } from "~/stores";
import type { Feature } from "~/types";

export default function useFilteredLocations(features: Feature[]) {
  const filters = useAtomValue(filterLocationsAtom);

  const filteredLocations = useMemo(() => {
    const { textSearch, categories, geoSearch, facets, geoSearchResults } =
      filters;
    const hasFacets = Object.values(facets).length > 0;
    if (!textSearch && !categories.length && !geoSearch && !hasFacets) {
      return features;
    }

    let baseFeatures = features;
    if (geoSearch) {
      baseFeatures = features.filter((f) => geoSearchResults?.includes(f.id));
    }

    return baseFeatures.filter((f) => {
      if (f.locked) return true;
      const searchKey = [
        f.title,
        f.summary,
        f.description,
        f.properties?.address,
      ]
        .filter(Boolean)
        .join("|");

      let matches = [];
      if (categories.length && f.category_id) {
        matches.push(categories.includes(f.category_id));
      }

      if (textSearch) {
        matches.push(
          searchKey.toLowerCase().includes(textSearch.toLowerCase())
        );
      }

      if (hasFacets) {
        Object.entries(facets).map(([key, values]) => {
          if (values.length) {
            const lookup = f.properties?.[key];
            if (typeof lookup === "string") {
              matches.push(
                lookup &&
                  values.some((v) =>
                    lookup
                      .split(",")
                      .map((l) => l.trim())
                      .includes(v)
                  )
              );
            } else {
              matches.push(lookup && values.includes(`${lookup}`));
            }
          }
          return "";
        });
      }

      if (matches) {
        return matches.every((m) => m);
      }

      return true;
    });
  }, [features, filters]);

  return filteredLocations;
}
